'use client';

import React from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faUserHairMullet } from '@fortawesome/pro-solid-svg-icons';
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons';
import {
  getStrategyMetricDisplay,
  getNotReferringSubtitle,
} from 'src/components/Strategy/Profiles/utils';
import { Checkbox, Tooltip } from 'src/components/PulseUI';

export interface StrategyMember {
  id?: string | number;
  provider_id?: string;
  name: string;
  specialty?: string;
  added_at?: string;
  metrics?: {
    patients?: number;
    sites?: number;
    step_0_claims?: number;
    [key: string]: number | undefined;
  } | null;
  metrics_v2?: Record<string, Record<string, number>>;
}

export interface StrategyMetricDefinition {
  id: number;
  label: string;
  display_options: { code: string; type: string; label: string }[];
  extras?: {
    partially_sending_denominator?: boolean;
    partially_sending_numerator?: boolean;
  };
}

export interface StrategyMetrics {
  results?: StrategyMetricDefinition[];
}

export interface StrategyMemberCardProps {
  member: StrategyMember;
  direction?: string | null;
  strategyMetrics?: StrategyMetrics;
  isActive?: boolean;
  isChecked?: boolean;
  onSelect?: () => void;
  onCheckboxChange?: () => void;
  onHover?: () => void;
  onHoverEnd?: () => void;
  type?: string;
  showAddToList?: boolean;
}

const StrategyMemberCard: React.FC<StrategyMemberCardProps> = ({
  member,
  strategyMetrics,
  isActive,
  isChecked,
  onSelect,
  onCheckboxChange,
  onHover,
  onHoverEnd,
  type,
  showAddToList = false,
}) => {
  const isNotReferring = type === 'NOT_REFERRING';
  const { value: metricValue, label: metricLabel } = getStrategyMetricDisplay({
    type,
    metrics: member.metrics,
    metricsV2: member.metrics_v2,
    strategyMetricResults: strategyMetrics?.results,
  });

  const notReferringSubtitle = isNotReferring
    ? getNotReferringSubtitle({
        metricsV2: member.metrics_v2,
        strategyMetricResults: strategyMetrics?.results,
      })
    : null;

  const formatDate = (date: string) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div
      className={`w-full px-4 py-3 bg-white hover:bg-sky-50 rounded-xl border overflow-hidden
      ${isActive ? 'border-sky-200' : 'border-neutral-200'}
        `}
      onClick={onSelect}
      onMouseEnter={onHover}
      onMouseLeave={onHoverEnd}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect?.();
        }
      }}
      aria-label={`View details for ${member.name}`}
      data-testid={`strategy-member-card-${member.provider_id || member.id}`}
    >
      <div className="w-full flex gap-2">
        {/* Checkbox Column */}
        {showAddToList && (
          <div
            className="flex-none flex items-start pt-1"
            onClick={(e) => e.stopPropagation()}
          >
            <Checkbox
              id={`select-member-${member.provider_id || member.id}`}
              size="sm"
              checked={isChecked}
              onChange={onCheckboxChange}
              aria-label={
                isChecked
                  ? `Deselect ${member.name} from the list`
                  : `Select ${member.name} to add to the list`
              }
            />
          </div>
        )}

        {/* Main Content Column */}
        <div className="flex-1 min-w-0 flex flex-col gap-3">
          <div className="self-stretch flex flex-col justify-start items-start gap-0.5">
            <div className="self-stretch h-6 inline-flex justify-between items-center gap-2">
              <Tooltip content={member.name}>
                <div
                  className="flex-1 max-w-48 justify-center text-primary text-base font-bold font-['Inter'] underline
                leading-6 whitespace-nowrap overflow-hidden text-ellipsis"
                >
                  {member.name}
                </div>
              </Tooltip>
              <div className="text-right justify-start text-tertiary text-xs font-normal font-['Inter'] leading-5">
                {formatDate(member.added_at) ||
                  (isNotReferring && notReferringSubtitle) ||
                  ''}
              </div>
            </div>
            <div className="flex justify-start items-center gap-1">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                <Icon
                  icon={faUserHairMullet}
                  className="text-secondary w-2.5 h-3"
                  size="sm"
                />
              </div>
              <div className="justify-start text-secondary text-xs font-normal font-['Inter'] leading-4">
                {member.specialty && (
                  <p className="text-xs text-secondary font-normal">
                    {member.specialty}
                  </p>
                )}
              </div>
            </div>
          </div>
          <div className="self-stretch inline-flex justify-between items-center gap-2">
            {metricValue != null ? (
              <div className="flex items-center gap-1 min-w-0 flex-1">
                <p
                  className="text-2xl font-extrabold text-teal-600 flex-shrink-0"
                  data-testid="metric-value"
                >
                  {metricValue}
                </p>
                <Tooltip content={metricLabel} className="min-w-0 overflow-hidden">
                  <p
                    className="text-sm font-normal text-tertiary pl-1 truncate"
                    data-testid="metric-label"
                  >
                    {metricLabel}
                  </p>
                </Tooltip>
              </div>
            ) : (
              <p className="text-sm font-normal text-tertiary">Unavailable</p>
            )}
            <div className="w-8 h-8 p-2 bg-teal-050 rounded-full flex justify-center items-center gap-2.5">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                <Icon
                  icon={faArrowRight}
                  size="sm"
                  className="text-secondary w-2.5 h-3"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StrategyMemberCard;
